<template>
  <Modal ref="dlg">
    <template #title>Ali želite oddati popravek poročila/poročil?</template>
    <template #body>Poročilo/poročila o plačilih bodo objavljena na portalu javnih naročil.</template>
    <template #footer>
      <div class="columns">
        <div class="column col-6 text-left">
          <button class="button" @click.prevent="submit(false)">Oddaj poročilo</button>
        </div>
        <div class="column col-6 text-right">
          <button class="button trans" @click.prevent="dlg.close()">Zapri</button>
        </div>
      </div>
    </template>
  </Modal>
  <div class="inner">
    <Nav />
    <div class="box">
      <h2>Popravek oddanega poročila</h2>
      <form>
        <div class="columns padding-bottom20">
          <div class="column col-3">
            <label class="text-bold form-label">Naročnik</label>
            <Select :data="clientList" v-model="filter.idClient" />
          </div>
          <div class="col-3 column">
            <label class="text-bold form-label">Številka javnega naročila</label>
            <InputText v-model="filter.jnTitle" />
          </div>
          <div class="col-3 column">
            <label class="text-bold form-label">Pogodba</label>
            <InputText v-model="filter.contract" />
          </div>
          <div class="col-3 column">
            <label class="text-bold form-label">Interna številka pogodbe</label>
            <InputText v-model="filter.internalId" />
          </div>
          <div class="col-3 column padding-top10">
            <label class="text-bold form-label">Znesek pogodbe</label>
            <InputNumeric :decimals="2" v-model="filter.amount" />
          </div>
          <div class="col-3 column padding-top10">
            <label class="text-bold form-label">Izvajalec pogodbe</label>
            <InputText v-model="filter.contractor" />
          </div>
          <div class="col-2 column padding-top47">
            <button class="button" @click.prevent="getContractList()">Išči</button>
          </div>
        </div>
      </form>
      <br />
      <div class="text-bold padding-bottom10 padding-top30">Seznam pogodb</div>
      <Tables :columns="cols" :outerPageNumber="filter.pagerPage" :data="contractList" @page-filter-change="getContractList($event)" :searchEnabled="false" :rowStyleList="[{ colRed: 'active === false' }]" />

      <div class="colRed" v-if="$appErrors.filter((x) => x.key === 'contract.id' && x.messageType === 'Required').length === 1">Izbira pogodbe je obvezna</div>

      <template v-if="data.contract.id">
        <div class="margin-bottom10 margin-top10">
          <ContractBox :data="contract" title="Izbrana pogodba" />
        </div>
        <TransactionList v-if="transactionList.length > 0" v-model="transactionList" :cancellation="true" :disabled="true" :canCancel="true" @onCancel="cancelTransaction" />
        <div v-else class="text-bold">Pogodba ne vsebuje transakcij.</div>
        <h2 class="padding-top10">Oddaj popravek</h2>
        <div class="padding-bottom5 text-right text-bold small"><a href @click.prevent="data.transactionList.push({ idPrev: null, amount: '', taxNumber: '', currency: { id: idEur } })">+ Dodaj plačilo</a></div>
        <TransactionList v-model="data.transactionList" :cancellation="true" />
        <InputWrapper :errors="errorMap($appErrors, 'transactionList')"> </InputWrapper>
      </template>
    </div>
    <div class="clearfix padding-bottom20 padding-top20">
      <button class="button" @click.prevent="submit(true)">Oddaj popravek</button>
      <button class="button trans pull-right" @click.prevent="$router.push({ name: 'reportList' })">Nazaj</button>
    </div>
  </div>
</template>

<script lang="ts">
import InputWrapper from '@/components/common/InputWrapper.vue'
import Select from '@/components/common/Select.vue'
import TransactionList from '@/components/TransactionList.vue'
import Modal from '@/components/common/Modal.vue'
import Nav from '@/components/Nav.vue'
import InputText from '@/components/common/InputText.vue'
import InputNumeric from '@/components/common/InputNumeric.vue'
import Tables from '@/components/common/TablesDb.vue'
import ContractBox from '@/components/ContractBox.vue'

import { useApi } from '@/lib/api'
import { errorMap } from '@/lib/filters'
import { defineComponent, ref, getCurrentInstance, markRaw } from 'vue'
import { useRouter } from 'vue-router'

const actions = markRaw({
  template: `<a href class="text-bold" @click.prevent="$parent.$parent.setContractId(data)">Izberi </a>`,
  props: {
    data: {}
  }
})

export default defineComponent({
  components: { Select, TransactionList, InputWrapper, Nav, Modal, InputText, InputNumeric, Tables, ContractBox },
  setup() {
    const _api = useApi()
    const _inst = getCurrentInstance()
    const _router = useRouter()
    const data = ref<any>({ contract: {}, transactionList: [{ idPrev: null, taxNumber: '', amount: '', currency: {} }] })
    const contract = ref({})
    const contractList = ref([])
    const transactionList = ref([])
    const clientList = ref([])
    const filter = ref({ pagerPage: 1, pagerPageSize: 20, pagerSort: 'np.id desc' })
    const idEur = ref()
    const dlg = ref()
    const cols = ref([
      { field: 'id', caption: '#', dbField: 'np.id', hidden: true },
      { field: 'jnTitle', caption: 'Št. JN', dbField: 'jn' },
      { field: 'title', caption: 'Pogodba', dbField: 'np.naziv' },
      { field: 'client.title', caption: 'Naročnik', dbField: 'narocnik' },
      { field: 'value', caption: 'Znesek pogodbe', dbField: 'vrednost', class: 'text-right-important', format: { type: 'number', format: 2 } },
      { field: 'dateFrom', caption: 'Poročanje od', format: { type: 'date', format: 'DD.MM.YYYY' }, dbField: 'datum_od' },
      { field: 'dateTo', caption: 'Poročanje do', format: { type: 'date', format: 'DD.MM.YYYY' }, dbField: 'datum_do' },
      //{ field: 'active', caption: 'Aktivnost', format: { type: 'yesno' }, dbField: 'NP.aktiven' },
      { caption: 'Akcija', component: actions, class: 'nowrap actions' }
    ])

    const getSifData = async () => {
      let res = await _api.get<any>('clientList')
      clientList.value = res.data.map((x: any) => ({
        id: x.id,
        text: x.title
      }))
      const n = { id: null, text: '- izberi -' }
      clientList.value.unshift(n as never)

      res = await _api.cache.get<any>('sifList?type=currency')
      idEur.value = res.data.filter((x: any) => x.text === 'EUR')[0].id
      data.value.transactionList[0].currency.id = idEur.value
    }

    const getContractList = async (e?: any) => {
      if (e) {
        filter.value.pagerPage = e.page
        filter.value.pagerSort = e.sort ? e.sort : 'np.id desc'
      } else if (filter.value.pagerPage > 1) {
        filter.value.pagerPage = 1
        return
      }
      const res = await _api.post<any>('contractList', filter.value, true)
      contractList.value = res.data
    }

    const submit = async (validate: boolean) => {
      const res = await _api.post<any>('reportWrite?validate=' + validate, data.value)
      if (res.status === 200) {
        if (validate) {
          dlg.value.open()
        } else {
          dlg.value.close()
          _inst?.appContext.config.globalProperties.emitter.emit('showToaster', { theme: 'green', text: 'Poročilo oddano.' })
          _router.push({ name: 'reportList', params: { id: res.data } })
        }
      }
    }

    const getTransactionList = async (id: any) => {
      const res = await _api.get<any>('transactionList?idContract=' + id, data.value)
      if (res.status === 200) {
        transactionList.value = res.data
      }
    }

    const cancelTransaction = (p: any) => {
      const trans = JSON.parse(JSON.stringify(p))
      trans.id = null
      trans.idPrev = p.id
      let t = data.value.transactionList[data.value.transactionList.length - 1]
      if (t && t.amount === '') {
        t.taxNumber = trans.taxNumber
        t.description = trans.description
        t.datePayment = trans.datePayment
        t.amount = trans.amount
        t.currency = trans.currency
        t.idPrev = trans.idPrev
      } else {
        data.value.transactionList.push(trans)
      }
    }

    const setContractId = (con: any) => {
      contract.value = { contract: con }
      data.value.contract.id = con.id
      getTransactionList(con.id)
    }

    getSifData()

    return { contract, setContractId, cols, getContractList, data, contractList, transactionList, clientList, submit, errorMap, getTransactionList, cancelTransaction, idEur, dlg, filter }
  }
})
</script>

<style lang="scss"></style>
